import { useState } from "react";
import { Invoice, getInvoicePaymentNoteText } from "../../types/invoice";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Download, Maximize2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import "../../assets/fonts/NotoSans-normal";

import { loadNotoSans } from "../../assets/fonts/loadNotoSans";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import logo from "../../assets/logo.svg";
import { isInterimInvoice } from "./EditInterimRentalDialog";

// Colors
const ORANGE = '#F59E0B';
const DARK_TEXT = '#1A1A2E';
const GRAY_BORDER = '#9E9E9E';
const LIGHT_GRAY = '#F8F8F8';
const FILL_COLOR = '#f5f5f5';

// Our company state ID (Karnataka = 12)
const OUR_STATE_ID = 12;

/** Interim without active IRN → Proforma; otherwise Tax Invoice. */
function getInvoiceDocumentTitle(invoice: Invoice): string {
  if (!isInterimInvoice(invoice)) return "TAX INVOICE";

  const hasIrn = Boolean(String(invoice.externalIrn ?? "").trim());
  const status = String(invoice.externalInvoiceStatus ?? "")
    .trim()
    .toLowerCase();
  const failedReason = String(invoice.externalFailedReason ?? "").trim();
  const isIrnCancelled = status === "cancelled";
  const isIrnFailed =
    status === "failed" || status === "failed_notified" || failedReason !== "";
  const hasActiveIrn = hasIrn && !isIrnCancelled && !isIrnFailed;

  return hasActiveIrn ? "TAX INVOICE" : "PROFORMA INVOICE";
}

function sameStateId(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean {
  if (a == null || a === "" || b == null || b === "") return false;
  return String(a).trim() === String(b).trim();
}

function resolveStateName(
  indiaState: any[] | undefined,
  code: string | number | null | undefined
): string {
  if (code == null || code === "") return "";
  const codeStr = String(code).trim();
  return (
    indiaState?.find((state: any) => String(state?.id).trim() === codeStr)
      ?.state_name || `State ${codeStr}`
  );
}

interface Props {
  invoice: Invoice;
  onBack?: () => void;
  indiaState?: any[];
}



// Helper function to get state name from code


// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to format date as DD MMM YYYY (e.g., 13 Dec 2025)
const formatDateWithMonth = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper function to format invoice number
const formatInvoiceNumber = (invoice: Invoice): string => {
  if (invoice.rsInvoiceCode) {
    return invoice.rsInvoiceCode;
  }
  if (invoice.displayId) {
    const num = typeof invoice.displayId === 'string' ? parseInt(invoice.displayId, 10) : invoice.displayId;
    if (!isNaN(num)) {
      return `INV-${String(num).padStart(3, '0')}`;
    }
    return `INV-${invoice.displayId}`;
  }
  if (invoice.invoiceId) {
    const num = typeof invoice.invoiceId === 'string' ? parseInt(invoice.invoiceId, 10) : invoice.invoiceId;
    if (!isNaN(num)) {
      return `INV-${String(num).padStart(3, '0')}`;
    }
    return `INV-${invoice.invoiceId}`;
  }
  return `INV-001`;
};

type GroupedInvoiceAsset = { itemName: string; qty: number };

const LEASE_RENTAL_WITH_ANNEXURE =
  "Lease Rental (Refer Annexure-I for Asset Details)";

function groupLineItemAssets(assets: unknown[]): GroupedInvoiceAsset[] {
  if (!Array.isArray(assets) || assets.length === 0) return [];

  const grouped = assets.reduce((acc: Record<string, GroupedInvoiceAsset>, asset: any) => {
    const assetLineId = asset.assetLineId || asset.assetId || asset.item || asset.name || "default";
    if (!acc[assetLineId]) {
      acc[assetLineId] = {
        itemName: asset.item || asset.name || "Item",
        qty: 0,
      };
    }
    acc[assetLineId].qty += asset.qty || 1;
    return acc;
  }, {});

  return Object.values(grouped);
}

function collectInvoiceAnnexureAssets(lineItems?: Invoice["lineItems"]): GroupedInvoiceAsset[] {
  if (!lineItems?.length) return [];

  const merged = new Map<string, GroupedInvoiceAsset>();
  for (const item of lineItems) {
    for (const asset of groupLineItemAssets(item.assets ?? [])) {
      const key = asset.itemName.trim() || "Item";
      const existing = merged.get(key);
      if (existing) {
        existing.qty += asset.qty;
      } else {
        merged.set(key, { itemName: key, qty: asset.qty });
      }
    }
  }
  return Array.from(merged.values());
}

function getInvoiceLineItemDescription(item: { assets?: unknown[] }): string {
  if (item.assets && Array.isArray(item.assets) && item.assets.length > 0) {
    return LEASE_RENTAL_WITH_ANNEXURE;
  }
  return "Lease Rental";
}

function formatAnnexureAssetDescription(asset: GroupedInvoiceAsset): string {
  return `${asset.itemName} Qty -${asset.qty}`;
}

const ANNEXURE_ROW_HEIGHT_MM = 5.5;
const ANNEXURE_CHARS_PER_LINE = 42;
/** Max estimated row units per column before starting the next column/page (HTML). */
const HTML_ANNEXURE_MAX_ROW_UNITS_PER_COLUMN = 20;

function estimateAnnexureRowUnits(asset: GroupedInvoiceAsset): number {
  const desc = formatAnnexureAssetDescription(asset);
  return Math.max(1, Math.ceil(desc.length / ANNEXURE_CHARS_PER_LINE));
}

function paginateAnnexureAssets(
  assets: GroupedInvoiceAsset[],
  maxUnitsPerColumn: number
): Array<{ left: GroupedInvoiceAsset[]; right: GroupedInvoiceAsset[]; leftStartIndex: number }> {
  const pages: Array<{ left: GroupedInvoiceAsset[]; right: GroupedInvoiceAsset[]; leftStartIndex: number }> = [];
  let idx = 0;

  const takeColumn = (): GroupedInvoiceAsset[] => {
    const column: GroupedInvoiceAsset[] = [];
    let units = 0;

    while (idx < assets.length) {
      const itemUnits = estimateAnnexureRowUnits(assets[idx]);
      if (column.length > 0 && units + itemUnits > maxUnitsPerColumn) break;
      column.push(assets[idx]);
      units += itemUnits;
      idx += 1;
    }

    return column;
  };

  // Flow per page: left column → right column; then next page repeats.
  while (idx < assets.length) {
    const leftStartIndex = idx;
    const left = takeColumn();
    const right = idx < assets.length ? takeColumn() : [];
    pages.push({ left, right, leftStartIndex });
  }

  return pages;
}

function AnnexureAssetColumns({
  left,
  right,
  leftStartIndex,
}: {
  left: GroupedInvoiceAsset[];
  right: GroupedInvoiceAsset[];
  leftStartIndex: number;
}) {
  const hasSecondColumn = right.length > 0;

  const renderColumn = (columnAssets: GroupedInvoiceAsset[], startIndex: number) => (
    <div className="flex flex-col text-[10px]">
      <div
        className="grid grid-cols-[44px_1fr] gap-2 font-bold pb-1 mb-1 border-b"
        style={{ borderColor: GRAY_BORDER }}
      >
        <span>S.No</span>
        <span>Item & Description</span>
      </div>
      {columnAssets.map((asset, index) => (
        <div key={`${asset.itemName}-${startIndex + index}`} className="grid grid-cols-[44px_1fr] gap-2 py-0.5">
          <span>{startIndex + index + 1}</span>
          <span>{formatAnnexureAssetDescription(asset)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`grid gap-6 ${hasSecondColumn ? "grid-cols-2" : "grid-cols-1"}`}>
      {renderColumn(left, leftStartIndex)}
      {hasSecondColumn ? renderColumn(right, leftStartIndex + left.length) : null}
    </div>
  );
}

function AnnexureAssetPages({
  assets,
  maxUnitsPerColumn,
}: {
  assets: GroupedInvoiceAsset[];
  maxUnitsPerColumn: number;
}) {
  const pages = paginateAnnexureAssets(assets, maxUnitsPerColumn);

  return (
    <>
      {pages.map((page, pageIndex) => (
        <div
          key={`annexure-page-${pageIndex}`}
          className="border p-4 mt-4"
          style={{
            borderColor: GRAY_BORDER,
            minHeight: "75vh",
            breakBefore: "page",
            pageBreakBefore: "always",
          }}
        >
          <div className="font-bold text-sm mb-4 text-center">
            {pageIndex === 0 ? "Annexure-I" : "Annexure-I (continued)"}
          </div>
          <AnnexureAssetColumns left={page.left} right={page.right} leftStartIndex={page.leftStartIndex} />
        </div>
      ))}
    </>
  );
}

const InvoicePdfViewer = ({ invoice, onBack, indiaState }: Props) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const getStateNameFromCode = (code: string | number | null | undefined): string =>
    resolveStateName(indiaState, code);

  const calculatePaymentSplits = () => {
    const apiLender = invoice.amountPayableToLender;
    const apiAstrafin = invoice.amountPayableToAstrafin;
    const hasApiLender = typeof apiLender === "number" && Number.isFinite(apiLender);
    const hasApiAstrafin = typeof apiAstrafin === "number" && Number.isFinite(apiAstrafin);

    if (!invoice.assignedToLender || !invoice.lenderName) {
      return {
        toAstrafin: hasApiAstrafin
          ? Math.max(0, apiAstrafin)
          : invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded,
        toLender: 0,
        lenderName: "",
      };
    }

    const gstTotal = (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) || 0;
    const tds = invoice.tdsRounded || 0;
    const base = invoice.baseRounded;

    let toLender = 0;
    let toAstrafin = 0;

    const fundingMode = invoice.fundingMode || "B";

    switch (fundingMode) {
      case "A":
        toLender = base;
        toAstrafin = gstTotal - tds;
        break;

      case "B":
        toLender = base + gstTotal - tds;
        toAstrafin = 0;
        break;

      case "C":
        toLender = base + gstTotal - tds;
        toAstrafin = 0;
        break;
    }

    return {
      toAstrafin: hasApiAstrafin ? Math.max(0, apiAstrafin) : toAstrafin,
      toLender: hasApiLender ? Math.max(0, apiLender) : toLender,
      lenderName: invoice.lenderName,
    };
  };

  function hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  }

  // Helper function to wrap address text intelligently
  const wrapAddressText = (text: string, maxLength: number = 40): string[] => {
    if (!text) return ["Address not available"];

    // If manual line breaks exist, respect them
    if (text.includes("\n")) {
      return text.split("\n").filter(line => line.trim());
    }

    const words = text.split(/\s+/);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      if ((currentLine + " " + word).trim().length > maxLength) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine += " " + word;
      }
    }

    if (currentLine.trim()) lines.push(currentLine.trim());

    return lines;
  };


  // Helper function to convert SVG to image data URL
  const svgToImageData = (svgUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        // Increase quality by scaling up the canvas
        const scale = 3; // 3x resolution for better quality
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Scale the context and draw the image
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png', 1.0)); // Maximum quality PNG
      };
      img.onerror = () => reject(new Error('Failed to load SVG'));
      img.src = svgUrl;
    });
  };

  // Helper function to convert emoji to image data URL
  const emojiToImageData = (emoji: string, fontSize: number = 10): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve('');
        return;
      }

      // Increase canvas size for better emoji rendering
      const scale = 2;
      canvas.width = fontSize * scale * 1.5;
      canvas.height = fontSize * scale * 1.8; // Increased height for taller emoji

      ctx.scale(scale, scale);
      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, canvas.width / (2 * scale), canvas.height / (2 * scale));

      resolve(canvas.toDataURL('image/png'));
    });
  };

  const toAbsoluteFileUrl = (filePath?: string): string | undefined => {
    if (!filePath) return undefined;
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const baseUrl = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
    const cleanPath = String(filePath).replace(/^\/+/, "");
    if (!baseUrl) return `/${cleanPath}`;
    return `${baseUrl}/${cleanPath}`;
  };

  const imageUrlToDataUrl = (
    url: string,
    options?: { maxWidth?: number; maxHeight?: number; quality?: number }
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }
        const sourceW = img.naturalWidth || img.width;
        const sourceH = img.naturalHeight || img.height;
        const maxWidth = options?.maxWidth ?? 700;
        const maxHeight = options?.maxHeight ?? 260;
        const ratio = Math.min(maxWidth / sourceW, maxHeight / sourceH, 1);
        const targetW = Math.max(1, Math.round(sourceW * ratio));
        const targetH = Math.max(1, Math.round(sourceH * ratio));

        canvas.width = targetW;
        canvas.height = targetH;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, targetW, targetH);

        // JPEG with medium quality keeps signature clear and PDF small.
        resolve(canvas.toDataURL("image/jpeg", options?.quality ?? 0.65));
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = url;
    });
  };

  const formatDisplayId = (displayId: string | undefined | null): string => {
    if (!displayId) return "—";
    const num = parseInt(displayId, 10);
    if (isNaN(num)) return displayId; // Return as-is if not a number
    if (num >= 1 && num <= 9) {
      return num.toString().padStart(2, '0');
    }
    return displayId; // Return as-is for 10+
  };

  // Client-side PDF generation — kept until API download is verified in production.
  const downloadPDF = async () => {
    const doc = new jsPDF();
    loadNotoSans(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = 15;

    const orangeColor: [number, number, number] = [232, 115, 62];
    const blackColor: [number, number, number] = [0, 0, 0];

    // Convert logo SVG to image
    const logoImageData = await svgToImageData(logo);

    // Convert emoji to image
    const emailIconData = await emojiToImageData('📧', 8);
    const signatureUrl = toAbsoluteFileUrl(invoice.iconImagePath);
    let signatureImageData = "";
    if (signatureUrl) {
      try {
        signatureImageData = await imageUrlToDataUrl(signatureUrl, {
          maxWidth: 700,
          maxHeight: 260,
          quality: 0.6,
        });
      } catch (e) {
        console.warn("Failed to load signature image for PDF:", e);
      }
    }

    // Determine if inter-state (IGST) or intra-state (CGST/SGST)
    const placeOfSupplyCode = invoice.billingAddress?.stateId;
    const placeOfSupplyName = getStateNameFromCode(placeOfSupplyCode);

    // Outer border for header section
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);

    doc.rect(
      margin,                 // x start (left)
      margin,                 // y start (top)
      pageWidth - margin * 2, // width
      pageHeight - margin * 2 // height
    );
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 35);

    // Logo area - add logo image
    if (logoImageData) {
      const logoWidth = 30; // Width in mm
      const logoHeight = 10; // Height in mm (maintaining aspect ratio)
      doc.addImage(logoImageData, 'PNG', margin + 5, yPos, logoWidth, logoHeight);
    } else {
      // Fallback to text if logo fails to load
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(41, 98, 255);
      doc.text("Astrafin", margin + 5, yPos + 8);
      doc.setTextColor(135, 206, 235);
      doc.text("★", margin + 31, yPos + 7);
      doc.setFontSize(6);
      doc.setTextColor(...blackColor);
      doc.setFont("helvetica", "normal");
      doc.text("NEOSOLUTIONS PVT. LTD.", margin + 5, yPos + 13);
    }

    // Company details - center aligned
    const centerX = pageWidth / 2;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("ASTRAFIN NEOSOLUTIONS PRIVATE LIMITED", centerX, yPos + 5, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("14th Floor, PH2, Bluebell, Klassik Landmark, Off. Sarjapur Road", centerX, yPos + 10, { align: 'center' });
    doc.text("Bengaluru Urban Karnataka 560035", centerX, yPos + 13, { align: 'center' });
    doc.text("GSTIN 29ABBCA4070B1Z3 | PAN ABBCA4070B", centerX, yPos + 18, { align: 'center' });
    // doc.text("India", centerX, yPos + 19, { align: 'center' });

    // Add phone and email with emoji icon
    const phoneText = "+91-7276308170";
    const emailText = "Email: platform@astrafin.org,";

    // Calculate text width to position emoji
    doc.setFontSize(7);
    const textWidth = doc.getTextWidth(phoneText + " " + emailText);
    const iconSize = 2.5; // Size in mm

    // Draw text with space for emoji
    const textX = centerX - (textWidth / 2);
    doc.text(phoneText, textX, yPos + 21);

    // Add emoji icon
    if (emailIconData) {
      const emojiX = textX + doc.getTextWidth(phoneText + " ");
      doc.addImage(emailIconData, 'PNG', emojiX, yPos + 19, iconSize, iconSize * 1.2); // Moved up more and increased height
    }

    // Draw email text after emoji
    const emailX = textX + doc.getTextWidth(phoneText + " ") + iconSize + 0.5;
    doc.text(emailText, emailX, yPos + 21);

    doc.text("mukul@astrafin.org", centerX, yPos + 24, { align: 'center' });
    // doc.text("astrafin.neo@gmail.com", centerX, yPos + 28, { align: 'center' });

    // Document title (Tax Invoice / Proforma for interim without IRN)
    const documentTitle = getInvoiceDocumentTitle(invoice);
    doc.setFontSize(documentTitle === "PROFORMA INVOICE" ? 14 : 18);
    doc.setFont("helvetica", "bold");
    // doc.setTextColor(...blackColor);
    doc.text(documentTitle, pageWidth - margin - 5, yPos + 15, { align: "right" });
    // doc.setTextColor(...blackColor);

    yPos = 45;

    // Invoice details row with thin border
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 25);
    doc.line(margin + 90, yPos, margin + 90, yPos + 25); // Vertical divider

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("#", margin + 3, yPos + 5);
    doc.setFont("helvetica", "bold");
    doc.text(`: ${formatInvoiceNumber(invoice)}`, margin + 25, yPos + 5);

    doc.setFont("helvetica", "normal");
    doc.text("Invoice Date", margin + 3, yPos + 10);
    doc.setFont("helvetica", "bold");
    doc.text(`: ${formatDate(invoice.invoiceDate)}`, margin + 25, yPos + 10);

    // doc.setFont("helvetica", "normal");
    // doc.text("Terms", margin + 3, yPos + 15);
    // doc.setFont("helvetica", "bold");
    // doc.text(`: Custom`, margin + 25, yPos + 15);

    doc.setFont("helvetica", "normal");
    doc.text("Due Date", margin + 3, yPos + 15);
    doc.setFont("helvetica", "bold");
    doc.text(`: ${formatDate(invoice.dueDate)}`, margin + 25, yPos + 15);

    doc.setFont("helvetica", "normal");
    doc.text("RS-No", margin + 3, yPos + 20);
    doc.setFont("helvetica", "bold");
    doc.text(`: ${formatDisplayId(invoice?.rentalScheduleDisplayId)}`, margin + 25, yPos + 20);

    // Place of Supply
    doc.setFont("helvetica", "normal");
    doc.text("Place Of Supply", margin + 93, yPos + 5);
    doc.setFont("helvetica", "bold");
    doc.text(`: ${placeOfSupplyName} `, margin + 120, yPos + 5);

    yPos = 70;

    // Bill To / Ship To - Get addresses from invoice or use defaults
    const billToAddress = (invoice as any).billingAddress || (invoice as any).billToAddress || {
      address: "Plot No 28B, 4th Main\nIndustrial Area, Bommasandra\nBengaluru\n560099 Karnataka\nIndia\nGSTIN 02AAACM9431E5ZT"
    };
    const shipToAddress = (invoice as any).shippingAddress || (invoice as any).shipToAddress || billToAddress;

    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.setFillColor(...hexToRgb(FILL_COLOR));
    doc.rect(margin, yPos, pageWidth - 2 * margin, 5, 'FD'); // 'FD' for fill and draw
    doc.setFillColor(255, 255, 255); // Reset to white for subsequent drawings
    doc.rect(margin, yPos + 5, pageWidth - 2 * margin, 30, 'FD'); // 'FD' for fill and draw
    doc.line(margin + 90, yPos, margin + 90, yPos + 35);  // Vertical divider
    doc.line(margin, yPos + 5, pageWidth - margin, yPos + 5);  // Horizontal divider

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Bill To", margin + 3, yPos + 3.5);
    doc.text("Ship To", margin + 94, yPos + 3.5);

    doc.setFont("helvetica", "bold");
    doc.text(invoice.customer.name.toUpperCase(), margin + 3, yPos + 11);
    doc.setFont("helvetica", "normal");

    // Split address into lines - wrap text intelligently
    const billToAddressText = billToAddress.address || "Address not available";
    const billToLines = wrapAddressText(billToAddressText, 45);

    let billToY = yPos + 15;
    billToLines.forEach((line: string) => {
      if (line.trim()) {
        doc.text(line.trim(), margin + 3, billToY);
        billToY += 4;
      }
    });


    // Use GSTIN from billingAddress if available, otherwise from customer

    if (billToAddress.gstin) {
      doc.text(`GSTIN : ${billToAddress.gstin}`, margin + 3, billToY);
    }



    // Ship To address
    // Split address into lines - wrap text intelligently
    const shipToAddressText = shipToAddress.address || "Address not available";
    const shipToLines = wrapAddressText(shipToAddressText, 40);

    let shipToY = yPos + 15;

    doc.setFont("helvetica", "bold");
    doc.text(invoice.customer.name.toUpperCase(), margin + 94, yPos + 11);
    doc.setFont("helvetica", "normal");

    shipToLines.forEach((line: string) => {
      if (line.trim()) {
        doc.text(line.trim(), margin + 94, shipToY);
        shipToY += 4;
      }
    });

    // Ship To GST not required


    yPos = 105;

    // Subject line
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 10.5);
    doc.setFont("helvetica", "normal");
    const lineItem = invoice.lineItems?.[0];
    const subjectText = lineItem
      ? `Lease Rental for the Period ${formatDateWithMonth(lineItem.fromDate)} to ${formatDateWithMonth(lineItem.toDate)}`
      : 'Lease Rental';
    doc.text("Subject : " + subjectText, margin + 3, yPos + 5.2);
    // doc.text(subjectText, margin + 3, yPos + 11);

    yPos = 115;
    // doc.setDrawColor(...hexToRgb(GRAY_BORDER));

    // Items table with orange header
    // Extract equipment descriptions from lineItems
    const itemsData = invoice.lineItems?.map((item, index) => {
      const itemDescription = getInvoiceLineItemDescription(item);

      return [
        index + 1,
        itemDescription,
        '997212',
        '1.00',
        `₹ ${item.baseAmount.toLocaleString('en-IN')}`,
        `₹ ${item.baseAmount.toLocaleString('en-IN')}`
      ];
    });

    // Set helvetica as default font for the table
    doc.setFont("helvetica", "normal");

    autoTable(doc, {
      startY: yPos,
      head: [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'Amount']],
      body: itemsData,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2,
        font: 'helvetica',
        lineWidth: 0.3,
        lineColor: [...hexToRgb(GRAY_BORDER)],
      },
      headStyles: {
        fillColor: FILL_COLOR,
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'helvetica',
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        font: 'helvetica',
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', fillColor: [255, 255, 255], font: 'helvetica' },
        1: { cellWidth: 83, fillColor: [255, 255, 255], font: 'helvetica', overflow: 'linebreak' },
        2: { cellWidth: 20, halign: 'center', fillColor: [255, 255, 255], font: 'helvetica' },
        3: { cellWidth: 15, halign: 'center', fillColor: [255, 255, 255], font: 'helvetica' },
        4: { cellWidth: 30, halign: 'right', fillColor: [255, 255, 255], font: 'NotoSans' },
        5: { cellWidth: 32, halign: 'right', fillColor: [255, 255, 255], font: 'NotoSans' }
      },
      didParseCell: (data: any) => {
        // Use NotoSans for Rate and Amount columns (4 and 5) which contain rupee symbol
        if (data.column.index === 4 || data.column.index === 5) {
          data.cell.styles.font = 'NotoSans';
        }
      },
      margin: { top: margin, left: margin, right: margin, bottom: margin },
    });
    yPos = (doc as any).lastAutoTable?.finalY || yPos + 25;

    const pagesAfterItemsTable = doc.getNumberOfPages();
    for (let p = 2; p <= pagesAfterItemsTable; p++) {
      doc.setPage(p);
      doc.setDrawColor(...hexToRgb(GRAY_BORDER));
      doc.setLineWidth(0.3);
      doc.rect(margin, margin, pageWidth - margin * 2, pageHeight - margin * 2);
    }
    doc.setPage(pagesAfterItemsTable);

    // Bottom section
    const gstTotal = (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) || 0;
    const gstRate = invoice.gstRate;;
    // const roundOff = Math.round((invoice.grandTotalRounded - (invoice.baseRounded + gstTotal - invoice.tdsRounded)) * 100) / 100;

    const bottomStartY = yPos;
    const bottomHeight = 95; // Increased height for notes

    // Draw outer border for bottom section
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    // doc.rect(margin, bottomStartY, pageWidth - 2 * margin, bottomHeight + 60);

    // Vertical divider between left content and summary
    const summaryX = pageWidth - margin - 80;
    doc.line(summaryX, bottomStartY, summaryX, bottomStartY + bottomHeight - 10);

    // Left side content
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Total In Words", margin + 3, bottomStartY + 6);
    doc.setFont("helvetica", "bolditalic");
    const wordsText = doc.splitTextToSize(numberToWords(invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded), summaryX - margin - 10);
    doc.text(wordsText, margin + 3, bottomStartY + 12);

    doc.setFont("helvetica", "normal");
    doc.text("Notes", margin + 3, bottomStartY + 26);

    const paymentSplits = calculatePaymentSplits();
    let notesY = bottomStartY + 32;

    const noteText = getInvoicePaymentNoteText(invoice, paymentSplits.lenderName);

    const noteLines = doc.splitTextToSize(noteText, summaryX - margin - 10);
    doc.text(noteLines, margin + 3, notesY);
    notesY += noteLines.length * 4 + 2;

    // Right side - Summary
    doc.setFontSize(8);
    let summaryY = bottomStartY + 6;
    const valueX = pageWidth - margin - 5;

    // Sub Total
    doc.setFont("helvetica", "normal");
    doc.text("Sub Total", summaryX + 3, summaryY);
    doc.setFont("NotoSans", "normal");
    doc.text(`₹ ${invoice.baseRounded.toLocaleString('en-IN')}`, valueX, summaryY, { align: 'right' });
    doc.setFont("helvetica", "normal");
    // doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);
    summaryY += 5;

    // IGST or CGST/SGST based on inter-state vs intra-state
    // If billing state is different from our state (12), use IGST (inter-state)
    // If billing state is same as our state (12), use CGST + SGST (intra-state)
    const billingStateId = invoice.billingAddress?.stateId;
    const isInterState = !sameStateId(billingStateId, OUR_STATE_ID);

    if (isInterState) {
      // Inter-state: Use IGST
      doc.text(`IGST (${gstRate}%)`, summaryX + 3, summaryY);
      doc.setFont("NotoSans", "normal");
      doc.text(`₹ ${gstTotal.toLocaleString('en-IN')}`, valueX, summaryY, { align: 'right' });
      doc.setFont("helvetica", "normal");
      // doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);
      summaryY += 5;
    } else {
      // Intra-state: Use CGST + SGST (split equally)
      const cgstAmount = gstTotal / 2;
      const sgstAmount = gstTotal / 2;
      doc.text(`CGST (${gstRate / 2}%)`, summaryX + 3, summaryY);
      doc.setFont("NotoSans", "normal");
      doc.text(`₹ ${cgstAmount.toLocaleString('en-IN')}`, valueX, summaryY, { align: 'right' });
      doc.setFont("helvetica", "normal");
      // doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);
      summaryY += 5;

      doc.text(`SGST (${gstRate / 2}%)`, summaryX + 3, summaryY);
      doc.setFont("NotoSans", "normal");
      doc.text(`₹ ${sgstAmount.toLocaleString('en-IN')}`, valueX, summaryY, { align: 'right' });
      doc.setFont("helvetica", "normal");
      // doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);
      summaryY += 5;
    }

    // Amount Withheld
    if (invoice.tdsRounded > 0) {
      doc.setFont("helvetica", "normal");
      doc.text(`Less: TDS @ ${invoice.tdsRate ?? 2}%`, summaryX + 3, summaryY);
      doc.setFontSize(7);
      // doc.text("(Section 194 IA)", summaryX + 3, summaryY + 4);
      doc.setFontSize(8);
      doc.setTextColor(220, 53, 69);
      doc.text(`(-) ${invoice.tdsRounded.toLocaleString('en-IN')}`, valueX, summaryY, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      // doc.line(summaryX, summaryY + 6, pageWidth - margin, summaryY + 6);
      summaryY += 8;
    }

    // Round off
    // doc.setFont("helvetica", "normal");
    // doc.text("Round off", summaryX + 3, summaryY);
    // doc.text(roundOff.toFixed(2), valueX, summaryY, { align: 'right' });
    // doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);
    // summaryY += 5;

    // Total
    // doc.setFont("helvetica", "bold");
    // Total
    doc.setFont("helvetica", "bold");
    doc.text("Total", summaryX + 3, summaryY);

    const totalAmount = (invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded).toLocaleString('en-IN');

    // Get width of amount
    doc.setFont("helvetica", "bold");
    const totalWidth = doc.getTextWidth(totalAmount);

    // Print number (right aligned)
    doc.text(totalAmount, valueX, summaryY, { align: "right" });

    // Print ₹ just before number
    doc.setFont("NotoSans", "normal");
    doc.text("₹", valueX - totalWidth - 2, summaryY);

    summaryY += 5;


    // Balance Due
    doc.setFont("helvetica", "bold");
    doc.text("Balance Due", summaryX + 3, summaryY);

    const balanceAmount = (invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded).toLocaleString('en-IN');

    // Get width
    doc.setFont("helvetica", "bold");
    const balanceWidth = doc.getTextWidth(balanceAmount);

    // Print number
    doc.text(balanceAmount, valueX, summaryY, { align: "right" });

    // Print ₹
    doc.setFont("NotoSans", "normal");
    doc.text("₹", valueX - balanceWidth - 2, summaryY);
    doc.setFont("helvetica", "normal");
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.line(summaryX, summaryY + 2, pageWidth - margin, summaryY + 2);

    // doc.line(summaryX, summaryY + 48.9, pageWidth - margin, summaryY + 48.9);
    doc.line(summaryX, bottomStartY + bottomHeight - 10, pageWidth - margin, bottomStartY + bottomHeight - 10);

    // doc.line(summaryX, bottomStartY, summaryX, bottomStartY + bottomHeight -10);


    // Authorized Signature section
    const signatureY = bottomStartY + bottomHeight;
    // doc.rect(margin, signatureY, pageWidth - 2 * margin, 25);
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (signatureImageData) {
      const lineLeftX = pageWidth - margin - 70;
      const lineRightX = pageWidth - margin - 5;
      const lineY = signatureY - 20;
      const lineWidth = lineRightX - lineLeftX;
      const signWidth = Math.min(50, lineWidth - 4);
      const signHeight = 30;
      const signNudgeRight = 5;
      const signX = Math.min(
        lineRightX - signWidth,
        lineLeftX + (lineWidth - signWidth) / 2 + signNudgeRight
      );
      const signY = lineY - signHeight - 0.5;
      doc.addImage(signatureImageData, "JPEG", signX, signY, signWidth, signHeight);
    }

    doc.text(
      "",
      pageWidth / 2,
      pageHeight - margin - 5,
      { align: "center" }
    );
    doc.text("Authorized Signature", pageWidth - margin - 43, signatureY - 18);
    doc.line(pageWidth - margin - 75, signatureY - 15, pageWidth - margin - 5, signatureY - 15);

    // Page 2: Payment Breakdown
    const paymentSplitsForPage2 = calculatePaymentSplits();
    doc.addPage();
    let page2Y = 15;
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.rect(margin, margin, pageWidth - margin * 2, pageHeight - margin * 2);

    // Page 2 header: logo centered, Payment Breakdown left on next line
    const logoWidth = 30;
    const logoHeight = 10;
    const logoX = (pageWidth - logoWidth) / 2;
    if (logoImageData) {
      doc.addImage(logoImageData, 'PNG', logoX, page2Y, logoWidth, logoHeight);
    } else {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(41, 98, 255);
      doc.text("Astrafin", pageWidth / 2, page2Y + 8, { align: "center" });
      doc.setTextColor(135, 206, 235);
      doc.text("★", pageWidth / 2 + 13, page2Y + 7, { align: "center" });
      doc.setFontSize(6);
      doc.setTextColor(...blackColor);
      doc.setFont("helvetica", "normal");
      doc.text("NEOSOLUTIONS PVT. LTD.", pageWidth / 2, page2Y + 13, { align: "center" });
    }
    page2Y += logoHeight + 12;
    const page2HeadingSize = 16;
    const page2BodySize = 10;
    const page2SubSize = 11;
    doc.setFontSize(page2HeadingSize);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Payment Breakdown", margin + 5, page2Y, { align: "left" });
    page2Y += 6;
    doc.setDrawColor(...hexToRgb(GRAY_BORDER));
    doc.setLineWidth(0.3);
    doc.line(margin, page2Y, pageWidth - margin, page2Y);
    page2Y += 10;

    const lineSpacing = 6;
    const sectionGap = 14;
    const bankLabels = ["Account Name", "Account Number", "IFSC", "Branch", "Bank Name"];
    const tableLeft = margin + 5;
    const labelColWidth = 52;
    const rowHeight = 7;
    const bankTableWidth = pageWidth - 2 * margin - 10;
    const PAYABLE_COLOR: [number, number, number] = [180, 155, 120];
    const BANK_TABLE_TEXT_COLOR: [number, number, number] = [100, 100, 100];

    const drawBankDetailsTable = (
      startY: number,
      values: string[]
    ): number => {
      const tableHeight = bankLabels.length * rowHeight;
      doc.setDrawColor(...hexToRgb(GRAY_BORDER));
      doc.setLineWidth(0.3);
      doc.rect(tableLeft, startY, bankTableWidth, tableHeight);
      doc.line(tableLeft + labelColWidth, startY, tableLeft + labelColWidth, startY + tableHeight);
      for (let i = 1; i < bankLabels.length; i++) {
        doc.line(tableLeft, startY + i * rowHeight, tableLeft + bankTableWidth, startY + i * rowHeight);
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(page2BodySize);
      bankLabels.forEach((label, i) => {
        const rowY = startY + (i + 0.5) * rowHeight + 1.2;
        doc.setTextColor(...blackColor);
        doc.text(label, tableLeft + labelColWidth / 2, rowY, { align: "center" });
        doc.setTextColor(...BANK_TABLE_TEXT_COLOR);
        doc.text(values[i] ?? "—", tableLeft + labelColWidth + 4, rowY);
      });
      doc.setTextColor(...blackColor);
      return startY + tableHeight;
    };

    // Same logic as Notes: when to show lender vs Astrafin (post-TDS, show only bank details)
    const showLenderBank = invoice.assignedToLender && paymentSplitsForPage2.lenderName && paymentSplitsForPage2.toLender > 0;

    // Section 1: Lender (only when assigned to lender and toLender > 0 – same as notes)
    if (showLenderBank) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(page2SubSize);
      doc.setTextColor(...PAYABLE_COLOR);
      doc.text(`Payable to ${paymentSplitsForPage2.lenderName}`, margin + 5, page2Y);
      doc.setTextColor(...blackColor);
      page2Y += lineSpacing + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(page2BodySize);
      doc.text("Amount: ", margin + 5, page2Y);
      doc.setFont("NotoSans", "normal");
      doc.text(`₹ ${paymentSplitsForPage2.toLender.toLocaleString("en-IN")}`, margin + 22, page2Y);
      doc.setFont("helvetica", "normal");
      page2Y += lineSpacing + 2;
      doc.setFont("helvetica", "bold");
      doc.text("Bank Details:", margin + 5, page2Y);
      doc.setFont("helvetica", "normal");
      page2Y += lineSpacing;
      page2Y = drawBankDetailsTable(page2Y, [
        invoice.lenderAccountHolderName ?? paymentSplitsForPage2.lenderName ?? "—",
        invoice.lenderAccountNumber ?? "—",
        invoice.lenderIfscCode ?? "—",
        invoice.lenderBranchName ?? "—",
        invoice.lenderBankName ?? "—",
      ]);
      page2Y += sectionGap;
    }

    // Section 2: Astrafin (amount + bank)
    if (paymentSplitsForPage2.toAstrafin > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(page2SubSize);
      doc.setTextColor(...PAYABLE_COLOR);
      doc.text("Payable to Astrafin Neosolutions Private Limited", margin + 5, page2Y);
      doc.setTextColor(...blackColor);
      page2Y += lineSpacing + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(page2BodySize);
      doc.text("Amount: ", margin + 5, page2Y);
      doc.setFont("NotoSans", "normal");
      doc.text(`₹ ${paymentSplitsForPage2.toAstrafin.toLocaleString("en-IN")}`, margin + 22, page2Y);
      doc.setFont("helvetica", "normal");
      page2Y += lineSpacing + 2;
      doc.setFont("helvetica", "bold");
      doc.text("Bank Details:", margin + 5, page2Y);
      doc.setFont("helvetica", "normal");
      page2Y += lineSpacing;
      page2Y = drawBankDetailsTable(page2Y, [
        "Astrafin Neosolutions Private Limited",
        "000205039252",
        "ICIC0000002",
        "ICICI Bank MG Road Branch",
        "ICICI Bank Limited",
      ]);
    }

    const annexureAssets = collectInvoiceAnnexureAssets(invoice.lineItems);
    if (annexureAssets.length > 0) {
      // Page 3+: annexure always starts on a new page after payment breakdown (page 2).
      doc.addPage();

      const colGap = 8;
      const pageBottomLimit = pageHeight - margin - 8;

      const drawAnnexurePageBorder = () => {
        doc.setDrawColor(...hexToRgb(GRAY_BORDER));
        doc.setLineWidth(0.3);
        doc.rect(margin, margin, pageWidth - margin * 2, pageHeight - margin * 2);
      };

      const setupAnnexurePageHeader = (isFirstPage: boolean) => {
        let y = margin + 10;

        if (isFirstPage) {
          const logoWidth = 30;
          const logoHeight = 10;
          const logoX = (pageWidth - logoWidth) / 2;
          if (logoImageData) {
            doc.addImage(logoImageData, "PNG", logoX, y, logoWidth, logoHeight);
            y += logoHeight + 10;
          } else {
            y += 4;
          }
        }

        doc.setFontSize(isFirstPage ? 14 : 12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(
          isFirstPage ? "Annexure-I" : "Annexure-I (continued)",
          pageWidth / 2,
          y,
          { align: "center" }
        );
        return y + 8;
      };

      const drawAnnexureColumn = (
        columnAssets: GroupedInvoiceAsset[],
        startIndex: number,
        leftX: number,
        colWidth: number,
        contentTopY: number,
        pageBottomY: number
      ): number => {
        if (columnAssets.length === 0) return 0;

        const sNoX = leftX + 8;
        const descX = leftX + 22;
        let y = contentTopY;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("S.No", sNoX, y, { align: "center" });
        doc.text("Item & Description", descX, y);
        y += 3;
        doc.setDrawColor(...hexToRgb(GRAY_BORDER));
        doc.setLineWidth(0.3);
        doc.line(leftX, y, leftX + colWidth, y);
        y += 5;

        doc.setFont("helvetica", "normal");
        let drawn = 0;

        for (let i = 0; i < columnAssets.length; i++) {
          const asset = columnAssets[i];
          const descLines = doc.splitTextToSize(
            formatAnnexureAssetDescription(asset),
            colWidth - 24
          ) as string[];
          const rowHeight = Math.max(ANNEXURE_ROW_HEIGHT_MM, descLines.length * 4.2);

          if (drawn > 0 && y + rowHeight > pageBottomY) break;

          doc.text(String(startIndex + i + 1), sNoX, y, { align: "center" });
          doc.text(descLines, descX, y);
          y += rowHeight;
          drawn += 1;

          if (y > pageBottomY) break;
        }

        return drawn;
      };

      let assetIdx = 0;
      let isFirstAnnexurePage = true;

      while (assetIdx < annexureAssets.length) {
        if (!isFirstAnnexurePage) {
          doc.addPage();
        }
        drawAnnexurePageBorder();

        const contentTopY = setupAnnexurePageHeader(isFirstAnnexurePage);
        const colWidth = (pageWidth - 2 * margin - colGap) / 2;

        const leftDrawn = drawAnnexureColumn(
          annexureAssets.slice(assetIdx),
          assetIdx,
          margin,
          colWidth,
          contentTopY,
          pageBottomLimit
        );
        assetIdx += leftDrawn;

        if (assetIdx < annexureAssets.length) {
          const rightDrawn = drawAnnexureColumn(
            annexureAssets.slice(assetIdx),
            assetIdx,
            margin + colWidth + colGap,
            colWidth,
            contentTopY,
            pageBottomLimit
          );
          assetIdx += rightDrawn;
        }

        isFirstAnnexurePage = false;
      }
    }

    doc.save(`Invoice-${formatInvoiceNumber(invoice)}.pdf`);
  };

  const downloadInvoicePdfFromApi = async () => {
    if (isDownloadingPdf) return;

    const invoiceId = invoice.invoiceId;
    if (!invoiceId) {
      toast.error("Invoice ID is missing");
      return;
    }

    setIsDownloadingPdf(true);
    try {
      const customerId = invoice.customerId || invoice.customer?.customerId;
      const params = new URLSearchParams({ download: "true" });
      const url = customerId
        ? `/rental-schedule/generate-rental-invoice-pdf/${encodeURIComponent(invoiceId)}/${encodeURIComponent(customerId)}?${params.toString()}`
        : `/rental-schedule/generate-rental-invoice-pdf/${encodeURIComponent(invoiceId)}?${params.toString()}`;

      const blob = await apiService.get<Blob>(url, { responseType: "blob" });

      if (blob.type === "application/json") {
        const errBody = JSON.parse(await blob.text());
        throw new Error(errBody?.message || "Failed to download invoice PDF");
      }

      const fileName = `Invoice-${formatInvoiceNumber(invoice)}.pdf`;
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      console.error("Failed to download invoice PDF:", error);
      toast.error("Failed to download invoice PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <>
      <Card className="w-full max-w-5xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-2">
            {onBack && (
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <CardTitle>Invoice {formatInvoiceNumber(invoice)}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadInvoicePdfFromApi}
              disabled={isDownloadingPdf}
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloadingPdf ? "Downloading..." : "Download PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsFullscreen(true)}>
              <Maximize2 className="h-4 w-4 mr-2" />
              Fullscreen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[70vh] overflow-auto">
            <div className="min-w-[800px] transform scale-90 origin-top p-4">
              <InvoiceContent invoice={invoice} indiaState={indiaState} />
            </div>
          </div>
        </CardContent>
      </Card>

      <FullscreenDialog
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        invoice={invoice}
        onDownload={downloadInvoicePdfFromApi}
        isDownloadingPdf={isDownloadingPdf}
        indiaState={indiaState}
      />
    </>
  );
};

const InvoiceContent = ({ invoice, indiaState }: { invoice: Invoice, indiaState?: any[] }) => {
  // const lenders = useAppStore((s: any) => s.lenders);

  // const getLenderBankDetails = () => {
  //   if (!invoice.assignedToLender || !invoice.lenderName) return null;
  //   return lenders.find((l: any) => l.companyName === invoice.lenderName);
  // };

  const getStateNameFromCode = (code: string | number | null | undefined): string =>
    resolveStateName(indiaState, code);

  // Helper function to wrap address text intelligently
  const wrapAddressText = (text: string, maxLength: number = 40): string[] => {
    if (!text) return ["Address not available"];

    // If manual line breaks exist, respect them
    if (text.includes("\n")) {
      return text.split("\n").filter(line => line.trim());
    }

    const words = text.split(/\s+/);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      if ((currentLine + " " + word).trim().length > maxLength) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine += " " + word;
      }
    }

    if (currentLine.trim()) lines.push(currentLine.trim());

    return lines;
  };

  const formatDisplayId = (displayId: string | undefined | null): string => {
    if (!displayId) return "—";
    const num = parseInt(displayId, 10);
    if (isNaN(num)) return displayId; // Return as-is if not a number
    if (num >= 1 && num <= 9) {
      return num.toString().padStart(2, '0');
    }
    return displayId; // Return as-is for 10+
  };

  const calculatePaymentSplits = () => {
    const apiLender = invoice.amountPayableToLender;
    const apiAstrafin = invoice.amountPayableToAstrafin;
    const hasApiLender = typeof apiLender === "number" && Number.isFinite(apiLender);
    const hasApiAstrafin = typeof apiAstrafin === "number" && Number.isFinite(apiAstrafin);

    if (!invoice.assignedToLender || !invoice.lenderName) {
      return {
        toAstrafin: hasApiAstrafin
          ? Math.max(0, apiAstrafin)
          : invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded,
        toLender: 0,
        lenderName: "",
      };
    }

    const gstTotal = (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) || 0;
    const tds = invoice.tdsRounded || 0;
    const base = invoice.baseRounded;

    let toLender = 0;
    let toAstrafin = 0;

    const fundingMode = invoice.fundingMode || "B";

    switch (fundingMode) {
      case "A":
        toLender = base;
        toAstrafin = gstTotal - tds;
        break;

      case "B":
        toLender = base + gstTotal - tds;
        toAstrafin = 0;
        break;

      case "C":
        toLender = base + gstTotal - tds;
        toAstrafin = 0;
        break;
    }

    return {
      toAstrafin: hasApiAstrafin ? Math.max(0, apiAstrafin) : toAstrafin,
      toLender: hasApiLender ? Math.max(0, apiLender) : toLender,
      lenderName: invoice.lenderName,
    };
  };

  const paymentSplits = calculatePaymentSplits();
  // const lenderDetails = getLenderBankDetails();
  const gstTotal = (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) || 0;
  const gstRate = invoice.gstRate;

  // Determine if inter-state
  const placeOfSupplyName = getStateNameFromCode(invoice.billingAddress?.stateId);

  const roundOff = Math.round((invoice.grandTotalRounded - (invoice.baseRounded + gstTotal - invoice.tdsRounded)) * 100) / 100;
  const lineItem = invoice.lineItems?.[0];
  const annexureAssets = collectInvoiceAnnexureAssets(invoice.lineItems);

  const billToAddress = (invoice as any).billingAddress || (invoice as any).billToAddress || {
    address: "Plot No 28B, 4th Main\nIndustrial Area, Bommasandra\nBengaluru\n560099 Karnataka\nIndia"
  };
  const shipToAddress = (invoice as any).shippingAddress || (invoice as any).shipToAddress || billToAddress;
  const documentTitle = getInvoiceDocumentTitle(invoice);

  return (
    <div className="bg-white p-6 text-black" style={{ fontFamily: 'Arial, sans-serif', width: '800px' }}>
      {/* Header */}
      <div className="border" style={{ borderColor: GRAY_BORDER }}>
        <div className="grid grid-cols-[130px_1fr_160px] items-start p-4">
          {/* Logo */}
          <div className="flex flex-col pr-3" style={{ borderColor: GRAY_BORDER }}>
            <img src={logo} alt="Astrafin Logo" className="h-8 w-auto" />
          </div>

          {/* Company Details - Center Aligned */}
          <div className="text-center text-[9px] leading-relaxed">
            <div className="font-bold text-[12px] mb-1">ASTRAFIN NEOSOLUTIONS PRIVATE LIMITED</div>
            <div>14th Floor, PH2, Bluebell, Klassik Landmark, Off. Sarjapur Road</div>
            <div>Bengaluru Urban Karnataka 560035</div>
            <div>GSTIN 29ABBCA4070B1Z3 | PAN ABBCA4070B</div>
            <div className="flex items-center justify-center gap-1">
              <span>+91-7276308170</span>
              <span>📧</span>
              <span>Email: platform@astrafin.org,</span>
            </div>
            <div>mukul@astrafin.org</div>
          </div>

          {/* TAX INVOICE / PROFORMA INVOICE (interim without IRN) */}
          <div className="text-right flex items-center justify-end h-full">
            <div
              className={`font-bold leading-tight ${
                documentTitle === "PROFORMA INVOICE" ? "text-xl" : "text-2xl"
              }`}
              style={{ color: "#000" }}
            >
              {documentTitle}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details Row */}
      <div className="border border-t-0" style={{ borderColor: GRAY_BORDER }}>
        <div className="grid grid-cols-[1fr_1fr]">
          {/* Left - Invoice Info */}
          <div className="border-r p-2 text-[10px]" style={{ borderColor: GRAY_BORDER }}>
            <div className="grid grid-cols-[80px_1fr] gap-y-1">
              <span>#</span>
              <span className="font-bold">: {formatInvoiceNumber(invoice)}</span>
              <span>Invoice Date</span>
              <span className="font-bold">: {formatDate(invoice.invoiceDate)}</span>
              {/* <span>Terms</span>
              <span className="font-bold">: Custom</span> */}
              <span>Due Date</span>
              <span className="font-bold">: {formatDate(invoice.dueDate)}</span>
              <span>RS-No</span>
              <span className="font-bold">: {formatDisplayId(invoice?.rentalScheduleDisplayId)}</span>
            </div>
          </div>

          {/* Right - Place of Supply */}
          <div className="p-2 text-[10px]">
            <div className="grid grid-cols-[100px_1fr]">
              <span>Place Of Supply</span>
              <span className="font-bold">: {placeOfSupplyName} </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To / Ship To */}
      <div className="border border-t-0" style={{ borderColor: GRAY_BORDER }}>
        <div className="grid grid-cols-2">
          <div className="border-r" style={{ borderColor: GRAY_BORDER }}>
            <div className="border-b px-2 py-1" style={{ borderColor: GRAY_BORDER, backgroundColor: FILL_COLOR }}>
              <span className="font-bold text-[10px]">Bill To</span>
            </div>
            <div className="p-2 text-[10px] leading-relaxed">
              <div className="font-bold uppercase">{invoice.customer.name}</div>
              {wrapAddressText(billToAddress.address || "Address not available", 40).map((line: string, idx: number) => (
                <div key={idx}>{line}</div>
              ))}
              {(billToAddress.gstin) && (
                <div>GSTIN {billToAddress.gstin}</div>
              )}
            </div>
          </div>
          <div>
            <div className="border-b px-2 py-1" style={{ borderColor: GRAY_BORDER, backgroundColor: FILL_COLOR }}>
              <span className="font-bold text-[10px]">Ship To</span>
            </div>
            <div className="p-2 text-[10px] leading-relaxed">
              <div className="font-bold uppercase">{invoice.customer.name}</div>
              {wrapAddressText(shipToAddress.address || "Address not available", 40).map((line: string, idx: number) => (
                <div key={idx}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subject Line */}
      <div className="border border-t-0 p-2 text-[10px]" style={{ borderColor: GRAY_BORDER }}>
        <div className="mb-1">Subject : {lineItem
          ? `Lease Rental for the Period ${formatDateWithMonth(lineItem.fromDate)} to ${formatDateWithMonth(lineItem.toDate)}`
          : 'Lease Rental'
        } </div>

      </div>

      {/* Items Table */}
      <table className="w-full text-[10px]" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: FILL_COLOR, color: 'black', borderColor: GRAY_BORDER }}>
            <th className="border p-2 text-left font-bold w-8" style={{ borderColor: GRAY_BORDER }}>#</th>
            <th className="border p-2 text-left font-bold" style={{ borderColor: GRAY_BORDER }}>Item & Description</th>
            <th className="border p-2 text-center font-bold w-20" style={{ borderColor: GRAY_BORDER }}>HSN/SAC</th>
            <th className="border p-2 text-center font-bold w-12" style={{ borderColor: GRAY_BORDER }}>Qty</th>
            <th className="border p-2 text-right font-bold w-24" style={{ borderColor: GRAY_BORDER }}>Rate</th>
            <th className="border p-2 text-right font-bold w-24" style={{ borderColor: GRAY_BORDER }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lineItems?.map((item, index) => {
            const itemDescription = getInvoiceLineItemDescription(item);

            return (
              <tr key={item.id}>
                <td className="border-l border-r border-b p-2 align-top" style={{ borderColor: GRAY_BORDER }}>{index + 1}</td>
                <td className="border-r border-b p-2 align-top" style={{ borderColor: GRAY_BORDER }}>
                  <div style={{ whiteSpace: "pre-line" }}>{itemDescription}</div>
                </td>
                <td className="border-r border-b p-2 text-center align-top" style={{ borderColor: GRAY_BORDER }}>997212</td>
                <td className="border-r border-b p-2 text-center align-top" style={{ borderColor: GRAY_BORDER }}>1.00</td>
                <td className="border-r border-b p-2 text-right align-top" style={{ borderColor: GRAY_BORDER }}> ₹ {item.baseAmount.toLocaleString('en-IN')}</td>
                <td className="border-r border-b p-2 text-right align-top" style={{ borderColor: GRAY_BORDER }}> ₹ {item.baseAmount.toLocaleString('en-IN')}</td>
              </tr>
            );
          }) || (
              <tr>
                <td className="border-l border-r border-b p-2 align-top" style={{ borderColor: GRAY_BORDER }}>1</td>
                <td className="border-r border-b p-2 align-top" style={{ borderColor: GRAY_BORDER }}>
                  <div>Lease Rental</div>
                  <div className="text-[9px] text-gray-600">Lease Rental from {formatDateWithMonth(invoice.invoiceDate)} to {formatDateWithMonth(invoice.dueDate)}</div>
                </td>
                <td className="border-r border-b p-2 text-center align-top" style={{ borderColor: GRAY_BORDER }}>997212</td>
                <td className="border-r border-b p-2 text-center align-top" style={{ borderColor: GRAY_BORDER }}>1.00</td>
                <td className="border-r border-b p-2 text-right align-top" style={{ borderColor: GRAY_BORDER }}> ₹ {invoice.baseRounded.toLocaleString('en-IN')}</td>
                <td className="border-r border-b p-2 text-right align-top" style={{ borderColor: GRAY_BORDER }}> ₹ {invoice.baseRounded.toLocaleString('en-IN')}</td>
              </tr>
            )}
        </tbody>
      </table>

      {/* Bottom Section */}
      <div className="border border-t-0" style={{ borderColor: GRAY_BORDER }}>
        <div className="grid grid-cols-[1fr_220px]">
          {/* Left Side */}
          <div className="border-r p-3 text-[10px]" style={{ borderColor: GRAY_BORDER }}>
            <div className="mb-2">
              <div>Total In Words</div>
              <div className="font-bold italic" style={{ color: '#B8860B' }}>
                {numberToWords(invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded)}
              </div>
            </div>

            <div className="mb-3">
              <div>Notes</div>
              {(() => {
                const noteText = getInvoicePaymentNoteText(invoice, paymentSplits.lenderName);
                if (!noteText) return null;
                return <div className="mb-1">{noteText}</div>;
              })()}
            </div>
          </div>

          {/* Right Side - Summary */}
          <div className="text-[10px]">
            <div className="flex justify-between p-1 " style={{ borderColor: GRAY_BORDER }}>
              <span className="font-normal">Sub Total</span>
              <span> ₹ {invoice.baseRounded.toLocaleString('en-IN')}</span>
            </div>
            {(() => {
              // IGST or CGST/SGST based on inter-state vs intra-state
              // If billing state is different from our state (12), use IGST (inter-state)
              // If billing state is same as our state (12), use CGST + SGST (intra-state)
              const billingStateId = invoice.billingAddress?.stateId;
              const isInterState = !sameStateId(billingStateId, OUR_STATE_ID);

              if (isInterState) {
                // Inter-state: Use IGST
                return (
                  <div className="flex justify-between p-1" style={{ borderColor: GRAY_BORDER }}>
                    <span className="font-normal">IGST ({gstRate}%)</span>
                    <span> ₹ {gstTotal.toLocaleString('en-IN')}</span>
                  </div>
                );
              } else {
                // Intra-state: Use CGST + SGST (split equally)
                return (
                  <>
                    <div className="flex justify-between p-1 " style={{ borderColor: GRAY_BORDER }}>
                      <span className="font-normal">CGST ({gstRate / 2}%)</span>
                      <span>₹ {(gstTotal / 2).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between p-1 " style={{ borderColor: GRAY_BORDER }}>
                      <span className="font-normal">SGST ({gstRate / 2}%)</span>
                      <span>₹ {(gstTotal / 2).toLocaleString('en-IN')}</span>
                    </div>
                  </>
                );
              }
            })()}
            {invoice.tdsRounded > 0 && (
              <div className="flex justify-between p-1 " style={{ borderColor: GRAY_BORDER }}>
                <span className="font-normal">
                  <div>Less: TDS @ {invoice.tdsRate ?? 2}%</div>
                </span>
                <span style={{ color: '#DC3545' }}>(-) {invoice.tdsRounded.toLocaleString('en-IN')}</span>
              </div>
            )}
            {/* <div className="flex justify-between p-2 border-b" style={{ borderColor: GRAY_BORDER }}>
              <span className="font-normal">Round off</span>
              <span>{roundOff.toFixed(2)}</span>
            </div> */}
            <div className="flex justify-between p-1  font-bold" style={{ borderColor: GRAY_BORDER }}>
              <span>Total</span>
              <span>₹{(invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between p-1 font-bold">
              <span>Balance Due</span>
              <span>₹{(invoice.baseRounded + (invoice.igstAmount ?? 0) + (invoice.cgstAmount ?? 0) + (invoice.sgstAmount ?? 0) - invoice.tdsRounded).toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Authorized Signature */}
      <div className="border border-t-0 p-4 h-24 flex flex-col justify-end items-end" style={{ borderColor: GRAY_BORDER }}>
        <div className="text-[9px] text-gray-600 w-full text-left mb-1">
          This is a system-generated invoice and does not require a signature.
        </div>
        <div className="border-t pt-1 px-8 text-[10px]" style={{ borderColor: GRAY_BORDER }}>Authorized Signature</div>
      </div>

      {/* Payment Breakdown - page 2 */}
      <div
        className="border border-t-0 p-4 mt-4"
        style={{ borderColor: GRAY_BORDER, breakBefore: "page", pageBreakBefore: "always" }}
      >
        <div className="text-center font-bold text-sm mb-4">Payment Breakdown</div>

        {invoice.assignedToLender && paymentSplits.lenderName && paymentSplits.toLender > 0 && (
          <div className="mb-6">
            <div className="font-bold text-[10px] mb-1">
              Payable to {paymentSplits.lenderName}
            </div>
            <div className="text-[10px] mb-1">
              Amount: <span className="font-medium">₹ {paymentSplits.toLender.toLocaleString("en-IN")}</span>
            </div>
            <div className="text-[10px] pl-2 space-y-0.5">
              <div>Bank Details:</div>
              <div>Account Name: {invoice.lenderAccountHolderName ?? paymentSplits.lenderName ?? "—"}</div>
              <div>Account Number: {invoice.lenderAccountNumber ?? "—"}</div>
              <div>IFSC: {invoice.lenderIfscCode ?? "—"}</div>
              <div>Bank Name: {invoice.lenderBankName ?? "—"}</div>
              {invoice.lenderBranchName && <div>Branch: {invoice.lenderBranchName}</div>}
            </div>
          </div>
        )}

        {paymentSplits.toAstrafin > 0 && (
          <div>
            <div className="font-bold text-[10px] mb-1">
              Payable to Astrafin Neosolutions Private Limited
            </div>
            <div className="text-[10px] mb-1">
              Amount: <span className="font-medium">₹ {paymentSplits.toAstrafin.toLocaleString("en-IN")}</span>
            </div>
            <div className="text-[10px] pl-2 space-y-0.5">
              <div>Bank Details:</div>
              <div>Account Name: Astrafin Neosolutions Private Limited</div>
              <div>Account Number: 000205039252</div>
              <div>IFSC: ICIC0000002</div>
              <div>Branch: ICICI Bank MG Road Branch</div>
              <div>Bank Name: ICICI Bank Limited</div>
            </div>
          </div>
        )}
      </div>

      {annexureAssets.length > 0 && (
        <AnnexureAssetPages assets={annexureAssets} maxUnitsPerColumn={HTML_ANNEXURE_MAX_ROW_UNITS_PER_COLUMN} />
      )}
    </div>
  );
};

const FullscreenDialog = ({
  isOpen,
  onClose,
  invoice,
  onDownload,
  isDownloadingPdf = false,
  indiaState
}: {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
  onDownload: () => void;
  isDownloadingPdf?: boolean;
  indiaState?: any[];
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[85vh] md:max-w-6xl max-h-[85vh] ms-auto w-[90%] rounded-t-xl p-0 overflow-hidden flex flex-col">
        <VisuallyHidden>
          <DialogTitle>Invoice {formatInvoiceNumber(invoice)}</DialogTitle>
          <DialogDescription>Full screen view of the invoice</DialogDescription>
        </VisuallyHidden>
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h2 className="text-lg font-semibold">Invoice {formatInvoiceNumber(invoice)}</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onDownload} disabled={isDownloadingPdf}>
              <Download className="h-4 w-4 mr-2" />
              {isDownloadingPdf ? "Downloading..." : "Download PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>
              Exit Fullscreen
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-8 flex justify-center">
            <InvoiceContent invoice={invoice} indiaState={indiaState} />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

function numberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  if (num === 0) return 'Zero Rupees Only';

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
  };

  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  const hundred = Math.floor(num);
  const paise = Math.round((num % 1) * 100);

  let result = '';
  if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
  if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
  if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
  if (hundred > 0) result += convertLessThanThousand(hundred);

  result = result.trim();
  if (result === '') result = 'Zero';

  result = 'Indian Rupee ' + result;
  if (paise > 0) result += ' and ' + convertLessThanThousand(paise) + ' Paise';

  return result + ' Only';
}

export default InvoicePdfViewer;
